import React, { useEffect, useMemo, useState } from "react";
import { trackCheckoutStep } from "./utils/analytics";
import {
  getCheckoutUrl,
  getLiveMode,
  getPreviewMode,
  getQueryVariable,
} from "./App";
import { getStripePromise } from "./stripePromiseTest";
import { LoadingIndicator } from "./LoadingIndicator";
import { ResetButton } from "./ResetButton";

const getStatusContent = (status) => {
  switch (status) {
    case "succeeded":
      return {
        eyebrow: "Payment complete",
        title: "Your payment was successful",
        intro:
          "The payment has been received and the booking will continue in GuideHub.",
        tone: "success",
      };
    case "processing":
      return {
        eyebrow: "Payment processing",
        title: "Your payment is still being processed",
        intro:
          "Some payment methods take a little longer to confirm. You can safely close this page and check again later.",
        tone: "processing",
      };
    case "requires_payment_method":
      return {
        eyebrow: "Payment not completed",
        title: "The payment could not be completed",
        intro:
          "Please try again with a different payment method or reopen the payment link from your booking.",
        tone: "error",
      };
    default:
      return {
        eyebrow: "Payment status",
        title: "We could not confirm the payment yet",
        intro:
          "Please refresh this page in a moment or reopen the payment link from your booking.",
        tone: "neutral",
      };
  }
};

const getFailureDetail = (paymentIntent, isPreviewMode) => {
  if (isPreviewMode) {
    return "The payment was not approved in this preview scenario. Please review the checkout and try again.";
  }

  if (paymentIntent?.last_payment_error?.message) {
    return paymentIntent.last_payment_error.message;
  }

  switch (paymentIntent?.status) {
    case "requires_payment_method":
      return "The bank or payment method did not approve the transaction. You can try again with the same booking link or use a different payment method.";
    case "canceled":
      return "The payment was cancelled before completion. You can reopen the checkout and try again.";
    default:
      return "We could not complete the payment. Please try again or reopen the payment link from your booking.";
  }
};

export const PaymentResultPage = () => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [paymentIntent, setPaymentIntent] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const tourId = getQueryVariable("tourId") || getQueryVariable("TourId");
  const clientSecret = getQueryVariable("payment_intent_client_secret");
  const isPreviewMode = getPreviewMode();
  const previewStatus = getQueryVariable("previewStatus") || "succeeded";
  const canRetry =
    status === "requires_payment_method" ||
    status === "canceled" ||
    status === "requires_action";

  useEffect(() => {
    const loadPaymentStatus = async () => {
      if (isPreviewMode) {
        const previewAmount = Number(getQueryVariable("previewPrice") || 480) * 100;
        setPaymentIntent({
          status: previewStatus,
          amount: previewAmount,
          currency: "pln",
          last_payment_error:
            previewStatus === "requires_payment_method"
              ? {
                  message:
                    "The payment was declined in this preview state so you can review the recovery layout.",
                }
              : null,
        });
        setStatus(previewStatus);
        setLoading(false);
        return;
      }

      if (!clientSecret) {
        setLoadError("Missing payment confirmation details.");
        setLoading(false);
        return;
      }

      try {
        const stripe = await getStripePromise(getLiveMode());

        if (!stripe) {
          throw new Error("Stripe could not be initialized.");
        }

        const { paymentIntent: retrievedIntent, error } =
          await stripe.retrievePaymentIntent(clientSecret);

        if (error) {
          throw error;
        }

        setPaymentIntent(retrievedIntent || null);
        setStatus(retrievedIntent?.status || null);

        trackCheckoutStep("payment_return_loaded", {
          payment_status: retrievedIntent?.status || "unknown",
          tourId,
        });
      } catch (error) {
        setLoadError(error.message || "Unable to load payment status.");
      } finally {
        setLoading(false);
      }
    };

    loadPaymentStatus();
  }, [clientSecret, isPreviewMode, previewStatus, tourId]);

  const content = useMemo(
    () => getStatusContent(status),
    [status],
  );
  const failureDetail = useMemo(
    () => getFailureDetail(paymentIntent, isPreviewMode),
    [isPreviewMode, paymentIntent],
  );

  if (loading) {
    return <LoadingIndicator text="Checking payment status..." />;
  }

  if (loadError) {
    return (
      <div className="PaymentStateCard PaymentStateCard--error">
        <div className="HeroEyebrow">Payment status</div>
        <h2>We could not load this payment result</h2>
        <p>{loadError}</p>
      </div>
    );
  }

  return (
    <div className="PaymentResultPage">
      <section className={`PaymentStateCard PaymentStateCard--${content.tone}`}>
        <div className="HeroEyebrow">{content.eyebrow}</div>
        <h2>{content.title}</h2>
        <p>{content.intro}</p>

        <div className="PaymentResultSummary">
          <div className="PaymentResultRow">
            <span>Status</span>
            <strong>{paymentIntent?.status || "Unknown"}</strong>
          </div>
          {paymentIntent?.amount ? (
            <div className="PaymentResultRow">
              <span>Total</span>
              <strong>
                {(paymentIntent.amount / 100).toFixed(2)}{" "}
                {(paymentIntent.currency || "pln").toUpperCase()}
              </strong>
            </div>
          ) : null}
          {tourId ? (
            <div className="PaymentResultRow">
              <span>Booking reference</span>
              <strong>{tourId}</strong>
            </div>
          ) : null}
        </div>

        {content.tone === "error" ? (
          <div className="PaymentFailureCard">
            <div className="PaymentFailureTitle">What went wrong</div>
            <p>{failureDetail}</p>
          </div>
        ) : null}

        <div className="PaymentResultActions">
          {canRetry ? (
            <ResetButton
              onClick={() => window.location.assign(getCheckoutUrl(tourId))}
              buttonText="Try payment again"
              className="ResetButton--primary"
            />
          ) : null}
          <ResetButton
            onClick={() => window.open("https://app.guidehub.eu/#/")}
            buttonText="Return to GuideHub"
            className={canRetry ? "ResetButton--secondary" : "ResetButton--primary"}
          />
          {tourId ? (
            <ResetButton
              onClick={() => window.open(`https://app.guidehub.eu/#/tour/${tourId}`)}
              buttonText="Open booking details"
              className="ResetButton--secondary"
            />
          ) : null}
        </div>
      </section>
    </div>
  );
};

export default PaymentResultPage;
